<template>
    <div
        v-gtm:label="oddsGtmLabel"
        v-gtm:@click
        v-debug:oddsId="odds.id"
        class="oddsBox notranslate"
        v-bind:class="{
            selected: isSelectedOdds,
            suspended: isSuspended,
        }"
        v-on:click="clickOdds"
    >
        <div v-if="isSuspended" class="oddsSuspend">
            <SvgIcon name="icon-system-odds-lock" />
        </div>
        <div
            v-else
            class="content"
            v-bind:class="{
                horizontal: layout === 'horizontal',
                vertical: layout === 'vertical',
                rise: priceChangeStatus === PriceChangeStatus.Rise,
                drop: priceChangeStatus === PriceChangeStatus.Drop,
            }"
        >
            <div class="oddsPoint">
                <strong v-if="betOption" class="left">{{ betOption }}</strong>
                <strong v-if="_isShowPoint" class="right">{{ pointDisplay }}</strong>
            </div>
            <div class="oddsValue">
                <strong class="price">{{ priceDisplay }}</strong>
                <SvgIcon
                    v-show="isPriceChange"
                    class="arrow"
                    name="icon-system-arrow-down"
                    v-bind:rotated="priceChangeStatus === PriceChangeStatus.Rise"
                />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { storeToRefs } from 'pinia';
    import { computed, toRefs } from 'vue';
    import SvgIcon from '@/components/common/SvgIcon.vue';
    import { getTournamentCenterOddsGtmLabel } from '@/components/tournamentCenter/tournamentCenterHelper';
    import { useBetPage } from '@/composable/useBetPage';
    import { usePriceChangeStatus } from '@/composable/usePriceChangeStatus';
    import { formatPointDecimal, formatPrice, getMarketGroupId, isShowPoint } from '@/core/lib/oddsHelper';
    import { EnumOddsStatus } from '@/core/oddsApi/oddsApiType';
    import { BetSlipType } from '@/interface/IBetSlip';
    import type { IOdds, IPrice } from '@/interface/IOdds';
    import { DisplaySection, PriceChangeStatus, TabName } from '@/interface/enum';
    import type { ITournamentEvent } from '@/interface/tournamentCenter';
    import { useBetSlipStore } from '@/store/betSlipStore';
    import { useLayoutStore } from '@/store/layoutStore';

    const props = withDefaults(
        defineProps<{
            odds: IOdds;
            price: IPrice;
            betOption?: string;
            event: ITournamentEvent;
            displaySection?: DisplaySection;
            layout: 'vertical' | 'horizontal';
        }>(),
        { displaySection: () => DisplaySection.Unknown, betOption: '' },
    );

    const { betPage } = useBetPage(props.displaySection);

    const { odds: _odds, price, event } = toRefs(props);

    const pointDisplay = computed(() => formatPointDecimal(_odds.value.point, _odds.value.marketType, price.value.option));

    const priceDisplay = computed(() => formatPrice(price.value.price, _odds.value.marketType));

    const isSuspended = computed(() => _odds.value.status === EnumOddsStatus.Suspend);

    const _isShowPoint = computed(() => isShowPoint(_odds.value.marketType, getMarketGroupId(_odds.value)));

    const priceValue = computed(() => price.value.price);
    const { priceChangeStatus, isPriceChange } = usePriceChangeStatus(priceValue);

    const { singleTicket } = storeToRefs(useBetSlipStore());
    const isSelectedOdds = computed(() => {
        if (!singleTicket.value) {
            return false;
        }

        return singleTicket.value.odds.id === _odds.value.id
            && singleTicket.value.priceOption.option === price.value.option;
    });

    const { isLayoutExpanded } = storeToRefs(useLayoutStore());
    const { toggleIsSportListExpanded, toggleBetSlipExpanded, toggleMyBetsExpanded, setBetSlipActiveTab } = useLayoutStore();
    const clickOdds = () => {
        if (_odds.value.status === EnumOddsStatus.Suspend) {
            return;
        }

        handleClickSingle();
        toggleIsSportListExpanded(false);
        toggleBetSlipExpanded(true);

        if (!isLayoutExpanded.value) {
            toggleMyBetsExpanded(false);
        }
    };

    const { addToSingleBetSlip, removeSingleBetSlip } = useBetSlipStore();

    function handleClickSingle() {
        if (isSelectedOdds.value) {
            removeSingleBetSlip();
        } else {
            addToSingleBetSlip(event.value, _odds.value, price.value, betPage.value, BetSlipType.Single);
        }

        setBetSlipActiveTab(TabName.Single);
    }

    const { clickGtmLabel, unclickGtmLabel } = getTournamentCenterOddsGtmLabel(props.displaySection);
    const oddsGtmLabel = computed(() => (isSelectedOdds.value ? unclickGtmLabel : clickGtmLabel));
</script>

<style lang="scss" scoped>
@import "@/components/tournamentCenter/TournamentSingleOdds";
</style>
